// components/ExperienceForm.js
import React, { useState, useEffect } from 'react';
import {
    Grid,
    FormControlLabel,
    Checkbox,
    Typography
} from '@mui/material';
import CommonFormField from '../../common/components/CommonFormField';
import CommonButton from '../../common/components/CommonButton';
import theme from '../../theme';

const ExperienceForm = ({ experience, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        company: '',
        startDate: '',
        endDate: '',
        currentJob: false
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (experience) {
            setFormData({
                title: experience.title || '',
                company: experience.company || '',
                startDate: experience.startDate || '',
                endDate: experience.endDate || '',
                currentJob: experience.currentJob || false
            });
        }
    }, [experience]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field
        if (type === 'checkbox' && e.target.checked) {
            setErrors(prev => ({ ...prev, endDate: '' }));
            setFormData(prev => ({
                ...prev,
                endDate: ''
            }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Job title is required';
        }
        if (!formData.company.trim()) {
            newErrors.company = 'Company name is required';
        }
        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        }
        if (!formData.currentJob && !formData.endDate) {
            newErrors.endDate = 'End date is required if not current job';
        }
        if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
            newErrors.endDate = 'End date must be after start date';
        }

        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validateForm();

        if (Object.keys(newErrors).length === 0) {
            onSave(formData);
        } else {
            setErrors(newErrors);
        }
    };


    const getMinDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 7); // Add 7 days
        return date.toISOString().split('T')[0];
    };


    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CommonFormField
                        label="Job Title"
                        type="email"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        error={errors.title}
                        placeholder="e.g., Senior Frontend Developer"
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <CommonFormField
                        label="Company"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        error={errors.company}
                        placeholder="e.g., Tech Corp"
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CommonFormField
                        label="Start Date"
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        error={errors.startDate}
                        placeholder="Select Start Date"
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CommonFormField
                        label="End Date"
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        error={errors.endDate}
                        placeholder="Select End Date"
                        disabled={formData.currentJob}
                        InputLabelProps={{ shrink: true }}
                        minDate={getMinDate()}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="currentJob"
                                checked={formData.currentJob}
                                onChange={handleInputChange}
                                color="primary"
                            />
                        }
                        label="I currently work here"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                        * All fields marked with * are required
                    </Typography>
                </Grid>

                <Grid item xs={12} display="flex" gap={2}>
                    <CommonButton
                        label="Cancel"
                        color={theme.palette.background.dark}
                        background="White"
                        border={`1px solid #0000001A`}
                        marginBottom="0px"
                        marginTop="0px"
                        handleClick={onCancel}
                    />
                    <CommonButton
                        label={`${experience ? 'Update' : 'Save'} Experience`}
                        color="white"
                        background={theme.palette.secondary.main}
                        marginBottom="0px"
                        marginTop="0px"
                        type="submit"
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default ExperienceForm;